import { HashLocationStrategy, LocationStrategy, registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpBackend, HttpClient, provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import de from '@angular/common/locales/de';
import en from '@angular/common/locales/en';
import it from '@angular/common/locales/it';
import { inject, LOCALE_ID, NgModule, provideAppInitializer } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { provideRouter } from '@angular/router';

import { ConfigOptions, ConfigOptionsService } from '@multimediasrl/emed-ui-config';
import {
  AuthSelector,
  AuthState,
  ConfigurazioneSelector,
  ConfigurazioneState,
  ENVIRONMENT,
  GestioneCancellazioneSelector,
  GestioneCancellazioneState,
  HttpInterceptorSelector,
  HttpInterceptorService,
  HttpInterceptorState,
  PrimengPreset,
  SharedCssService,
  SharedThemeService,
  SistemaSelector,
  SistemaState,
  ToastService,
  UtenteService
} from '@multimediasrl/emed-ui-shared';

import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { NgxsLoggerPluginModule } from '@ngxs/logger-plugin';
import { NgxsModule } from '@ngxs/store';

import { TranslateLoader, TranslateModule } from '@ngx-translate/core';

import { AuthInterceptor, AuthModule, LogLevel, OpenIdConfiguration, StsConfigHttpLoader, StsConfigLoader } from 'angular-auth-oidc-client';

import { NgHttpLoaderComponent, pendingRequestsInterceptor$ } from 'ng-http-loader';

import { MultiTranslateHttpLoader } from 'ngx-translate-multi-http-loader';

import { ConfirmationService, MessageService } from 'primeng/api';
import { providePrimeNG } from 'primeng/config';

import { map } from 'rxjs';

import { environment } from '../environments/environment';

import { AppRoutingModule, routes } from './app-routing.module';
import { AppComponent } from './app.component';

import { EmedHomeComponent } from './components/emed-home/emed-home.component';
import { SharedModule } from './modules/shared/shared.module';
import { CognitoTokenInterceptor } from './modules/dispatcher/interceptor/cognito-token.interceptor';
import { CognitoSelector } from './modules/dispatcher/state/selectors/cognito-selector';
import { CognitoState } from './modules/dispatcher/state/store/cognito.state';

registerLocaleData(it, 'it');
registerLocaleData(de, 'de');
registerLocaleData(en, 'en');

export function HttpLoaderFactory(_httpBackend: HttpBackend) {
  return new MultiTranslateHttpLoader(_httpBackend, [
    { prefix: './assets/i18n/', suffix: '.json' },
    { prefix: './assets/emed-ui-shared/i18n/', suffix: '.json' }
  ]);
}

export function amplifyConfigFactory(themeService: SharedThemeService): () => Promise<void> {
  themeService.switchTheme(localStorage.getItem(environment.localStorageID + '-app-theme'));
  return () => Promise.resolve();
}

export const httpLoaderFactory = (httpClient: HttpClient) => {
  const configOptions = httpClient.get<ConfigOptions>('./assets/configuration/config.json').pipe(
    map((customConfig: any) => {
      const config: OpenIdConfiguration = {
        authority: environment.authority,
        redirectUrl: customConfig.redirectUrl,
        postLogoutRedirectUri: environment.logoutUrl,
        unauthorizedRoute: '/main/dispatcher/unauthorized',
        forbiddenRoute: '/main/dispatcher/unauthorized',
        clientId: customConfig.clientId,
        scope: 'openid profile email',
        responseType: 'code',
        silentRenew: true,
        secureRoutes: [customConfig.secureRoutes],
        useRefreshToken: true,
        renewTimeBeforeTokenExpiresInSeconds: 30,
        logLevel: LogLevel.Debug
      };
      return config;
    })
  );

  return new StsConfigHttpLoader(configOptions);
};

@NgModule({
  declarations: [AppComponent, EmedHomeComponent],
  imports: [
    AppRoutingModule,
    AuthModule.forRoot({
      loader: {
        provide: StsConfigLoader,
        useFactory: httpLoaderFactory,
        deps: [HttpClient]
      }
    }),
    BrowserAnimationsModule,
    BrowserModule,
    FormsModule,
    ReactiveFormsModule,
    NgxsLoggerPluginModule.forRoot(),
    NgxsReduxDevtoolsPluginModule.forRoot(),
    NgxsModule.forRoot([SistemaState, AuthState, CognitoState, HttpInterceptorState, ConfigurazioneState, GestioneCancellazioneState], {
      developmentMode: !environment.production,
      selectorOptions: { suppressErrors: false }
    }),
    SharedModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpBackend]
      },
      defaultLanguage: 'it'
    }),
    NgHttpLoaderComponent
  ],
  providers: [
    provideAppInitializer(async () => {
      const sharedThemeService = inject(SharedThemeService);
      await amplifyConfigFactory(sharedThemeService);
    }),
    provideHttpClient(withInterceptorsFromDi(), withInterceptors([pendingRequestsInterceptor$])),
    provideRouter(routes),
    //provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        preset: PrimengPreset,
        options: {
          darkModeSelector: '.my-app-dark'
        }
      }
    }),
    { provide: LOCALE_ID, useValue: 'it' },
    { provide: ENVIRONMENT, useValue: environment },
    { provide: HTTP_INTERCEPTORS, useClass: HttpInterceptorService, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: CognitoTokenInterceptor, multi: true },
    { provide: 'IMyCssService', useClass: SharedCssService },
    { provide: 'IThemeService', useClass: SharedThemeService },
    SharedCssService,
    SharedThemeService,
    ConfigOptionsService,
    UtenteService,
    AuthSelector,
    CognitoSelector,
    ConfigurazioneSelector,
    GestioneCancellazioneSelector,
    HttpInterceptorSelector,
    SistemaSelector,
    MessageService,
    ConfirmationService,
    ToastService
  ],
  bootstrap: [AppComponent]
})
export class AppModule {}
